import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';

const AnimalCard = ({ animal }) => {
  const navigate = useNavigate();
  
  const defaultImages = {
    Dog: 'https://images.unsplash.com/photo-1644316549560-a0d17dd39978?auto=format&fit=crop&w=800&q=80',
    Cat: 'https://images.unsplash.com/photo-1765176096027-57b4c46b972f?auto=format&fit=crop&w=800&q=80'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => navigate(`/animal/${animal.id}`)}
      data-testid={`animal-card-${animal.id}`}
      className="bg-white rounded-2xl shadow-sm border border-orange-100 overflow-hidden hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 cursor-pointer group hover:-translate-y-1"
    >
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-orange-100 to-orange-50">
        <img
          src={animal.photo || defaultImages[animal.species]}
          alt={animal.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 flex gap-2">
          {animal.vaccinated && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200" data-testid="vaccinated-badge">
              <Check className="w-3 h-3 inline mr-1" />
              Vaccinated
            </span>
          )}
          {animal.sterilized && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700 border border-violet-200" data-testid="sterilized-badge">
              <Check className="w-3 h-3 inline mr-1" />
              Sterilized
            </span>
          )}
        </div>
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/90 text-primary backdrop-blur-sm" data-testid="species-badge">
            {animal.species}
          </span>
        </div>
      </div>
      
      <div className="p-5">
        <h3 className="text-xl font-bold text-text-primary mb-2 font-heading" data-testid="animal-name">{animal.name}</h3>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="text-sm bg-orange-50 text-text-secondary px-3 py-1 rounded-full font-medium" data-testid="animal-gender">
            {animal.gender}
          </span>
          <span className="text-sm bg-orange-50 text-text-secondary px-3 py-1 rounded-full font-medium" data-testid="animal-colour">
            {animal.colour}
          </span>
          <span className={`text-sm px-3 py-1 rounded-full font-bold ${
            animal.temperament === 'Friendly'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`} data-testid="animal-temperament">
            {animal.temperament}
          </span>
        </div>
        
        <div className="flex items-center text-text-secondary text-sm" data-testid="animal-area">
          <MapPin className="w-4 h-4 mr-1 text-primary" />
          {animal.area}
        </div>
        
        {animal.remarks && (
          <p className="mt-3 text-sm text-text-secondary line-clamp-2" data-testid="animal-remarks">
            {animal.remarks}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default AnimalCard;